// src/icons/index.js
import {
    HomeIcon,
    UsersIcon,
    Cog6ToothIcon,
    UserGroupIcon,
    LockClosedIcon,
    UserPlusIcon,
    PencilSquareIcon,
    TrashIcon,
    PuzzlePieceIcon as GroupIcon,
    KeyIcon,
    ArrowLeftOnRectangleIcon,
    UserCircleIcon,
    IdentificationIcon,
    CogIcon,
    BookmarkIcon,
    PlusIcon,
    XCircleIcon,
    ChevronLeftIcon,
    EnvelopeIcon,
    BellIcon,
    BellAlertIcon,
    GlobeAltIcon,
    FlagIcon,
    MapIcon,
    MapPinIcon,
    Squares2X2Icon,
    CubeIcon,
    ArrowUpTrayIcon,
    PowerIcon,
    BuildingStorefrontIcon,
    TagIcon,
    ShoppingBagIcon,
    ScaleIcon,
    ArchiveBoxIcon,
    ClipboardDocumentListIcon,
    ReceiptPercentIcon,
    RectangleGroupIcon,
    BanknotesIcon,
    UserIcon,
    ClockIcon,
    DocumentTextIcon,
    ShoppingCartIcon,
    SparklesIcon,
    GiftIcon,
    ViewColumnsIcon,
    ReceiptRefundIcon,
    TicketIcon,
    ChartBarIcon,
    ArrowDownTrayIcon,
    PrinterIcon,
    AdjustmentsHorizontalIcon,
    LanguageIcon,
    QueueListIcon,
    CalculatorIcon,
    BookOpenIcon,
    ClipboardDocumentCheckIcon,
    CreditCardIcon,
    ListBulletIcon,
    ScissorsIcon,
    CalendarDaysIcon,
    TruckIcon,
    DocumentCheckIcon,
    ArrowsRightLeftIcon,
    BuildingOffice2Icon,
    TableCellsIcon,
    FireIcon,
    ArrowPathIcon,
    ComputerDesktopIcon,
    ClipboardDocumentIcon,
    InboxStackIcon,
    QrCodeIcon,
    MagnifyingGlassIcon,
  } from '@heroicons/react/24/outline';

  export const ICONS = {
    Dashboard: HomeIcon,           // Dashboard
    Users: UsersIcon,              // Users list
    AddUser: UserPlusIcon,         // Add user
    EditUser: PencilSquareIcon,    // Edit user
    DeleteUser: TrashIcon,         // Delete user
    Groups: GroupIcon,             // Groups list
    Permissions: KeyIcon,          // Permissions
    Settings: Cog6ToothIcon,       // Settings
    Logout: ArrowLeftOnRectangleIcon, // Logout
    Profile: UserCircleIcon,       // Profile
    Lock: LockClosedIcon,          // General lock / permission
    UserManagement: IdentificationIcon,
    Config: CogIcon,
    Save:BookmarkIcon,
    Plus:PlusIcon,
    Close:XCircleIcon,
    ChevronLeft:ChevronLeftIcon,
    Email:EnvelopeIcon,
    Bell:BellIcon,
    BellAlert:BellAlertIcon,
    Geography: GlobeAltIcon,
    Countries: FlagIcon,
    Zones: MapIcon,
    Cities: MapPinIcon,
    GeoZones: Squares2X2Icon,
    Modules: CubeIcon,
    Install: ArrowUpTrayIcon,
    Power: PowerIcon,
    POS: ShoppingBagIcon,
    Branches: BuildingStorefrontIcon,
    Categories: TagIcon,
    Units: ScaleIcon,
    Products: ShoppingBagIcon,
    Inventory: ArchiveBoxIcon,
    StockMovements: ClipboardDocumentListIcon,
    Taxes: ReceiptPercentIcon,
    TaxGroups: RectangleGroupIcon,
    Currencies: BanknotesIcon,
    Customers: UserIcon,
    Shifts: ClockIcon,
    Orders: DocumentTextIcon,
    Checkout: ShoppingCartIcon,
    Discounts: SparklesIcon,
    BundleOffers: GiftIcon,
    CustomFields: ViewColumnsIcon,
    Returns: ReceiptRefundIcon,
    Vouchers: TicketIcon,
    Reports: ChartBarIcon,
    Download: ArrowDownTrayIcon,
    Print: PrinterIcon,
    Customize: AdjustmentsHorizontalIcon,
    Translate: LanguageIcon,
    // Logs: added alongside this pass's "System Logs" screen — QueueListIcon
    // rather than reusing DocumentTextIcon (already Orders' icon above) so
    // the two stay visually distinct in the sidebar.
    Logs: QueueListIcon,
    // Accounting — Chart of Accounts / Journal Entries / Expenses / Expense
    // Categories, all new with this pass. Each gets its own icon rather
    // than reusing something above so the Accounting section reads clearly
    // in the sidebar even at a glance.
    Accounting: CalculatorIcon,
    ChartOfAccounts: BookOpenIcon,
    JournalEntries: ClipboardDocumentCheckIcon,
    Expenses: CreditCardIcon,
    ExpenseCategories: ListBulletIcon,
    // Services & Appointments module — a scissors icon reads as
    // "salon/beauty service" at a glance, distinct from every icon above.
    Services: ScissorsIcon,
    // Appointments — the booking calendar, its own separate screen from
    // Services (see appointment.go's own doc comment).
    Appointments: CalendarDaysIcon,
    // Suppliers — the other side of Customers (UserIcon above): who a
    // branch buys stock FROM. A truck reads as "incoming goods/purchasing"
    // at a glance, distinct from Customers' person icon.
    Suppliers: TruckIcon,
    // Purchase Orders — the document a branch sends TO a Supplier. A
    // checked document reads as "approved/fulfilled paperwork", distinct
    // from JournalEntries (ClipboardDocumentCheckIcon) and Orders
    // (DocumentTextIcon) above.
    PurchaseOrders: DocumentCheckIcon,
    // Stock Transfers — moves stock between two branches rather than
    // bringing it in from a Supplier (PurchaseOrders' DocumentCheckIcon) or
    // adjusting it by hand (Inventory's ArchiveBoxIcon above). Two opposing
    // arrows read as "moving between two places" at a glance.
    StockTransfers: ArrowsRightLeftIcon,
    // Sections & Tables — the Phase 3 (restaurant) foundation: a named
    // dining area within a Branch, and the physical tables grouped under
    // one. Distinct from Branches (BuildingStorefrontIcon) and GeoZones
    // (Squares2X2Icon) above.
    Sections: BuildingOffice2Icon,
    Tables: TableCellsIcon,
    // Kitchen Display — the live board kitchen staff work from (see
    // ListKitchenOrdersHandler in order_handler.go). A flame reads as
    // "kitchen/cooking" at a glance, distinct from every icon above.
    KitchenDisplay: FireIcon,
    Refresh: ArrowPathIcon,
    // Thermal Receipt Printing (ESC/POS) via Print-Agent — see
    // models.PrintAgent/Printer/ReceiptTemplate on the backend. PrintAgents
    // reuses no other icon (a computer running the agent program, distinct
    // from Printers below, which is the physical device it talks to).
    PrintAgents: ComputerDesktopIcon,
    Printers: PrinterIcon,
    ReceiptTemplate: ClipboardDocumentIcon,
    // Print Jobs — the queue of PrintJob rows (see models.PrintJob), the
    // admin's own view/retry/delete control over what's stuck, failed, or
    // still pending. A stacked-inbox icon reads as "job queue", distinct
    // from Printers (the physical device, PrinterIcon) and PrintAgents
    // (the machine running the relay, ComputerDesktopIcon) above.
    PrintJobs: InboxStackIcon,
    // Print Barcodes — PrintBarcodes.js, the bulk product-label-printing
    // screen (see label_print_handler.go). A scan-code icon reads as
    // "barcode/label" at a glance, distinct from every icon above.
    PrintBarcodes: QrCodeIcon,
    // Search — the header's global "search pages & features" box
    // (components/UI/HeaderSearch.jsx).
    Search: MagnifyingGlassIcon,
  };
